import React, { useState, useEffect } from "react";
import * as yup from "yup";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGetCommonBackgroundQuery } from "../features/api/publicApiSlice";
import FormSelect from "../components/form/FormSelect";

import {
  useTheme,
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  FormControl,
  Container,
  Grid,
  Divider,
  List,
  ListItem,
  Modal,
  Toolbar,
} from "@mui/material";
import { Close, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import FormTextField from "../components/form/FormTextField";
import FormPhoneNumber from "../components/form/FormPhoneNumber";
import { matchIsValidTel } from "mui-tel-input";
import FormDatePicker from "../components/form/FormDatePicker";
import Image from "../components/common/Image";
import {
  useLazyGetOtpQuery,
  useRegisterAccountMutation,
  useGetPublicBankAccountsQuery,
  useRegisterAccountWithBankMutation
} from "../features/api/publicApiSlice";
import { setAlert } from "../features/slices/alertSlice";
import { setCredentials } from "../features/slices/authSlice";
import { selectCurrentToken } from "../features/slices/authSlice";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import ImageCarousel from "../components/common/ImageCarousel";
import * as Constant from "../features/constant";
import Cookies from "js-cookie";
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import {
  useAddFingerprintMutation,
} from "../features/api/publicApiSlice";
import { lighten, darken, alpha } from "@material-ui/core";
import PersonIcon from '@mui/icons-material/Person';
import KeyIcon from '@mui/icons-material/Key';

// pending adding request mobile otp logic
const Register = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletView = useMediaQuery(theme.breakpoints.down("md"));
  const [detailValue, setDetailValue] = useState({ onThisPage: true });
  const [profileValue, setProfileValue] = useState({});
  const token = useSelector(selectCurrentToken);
  const lang = localStorage.getItem("language");
  const [searchParams, setSearchParams] = useSearchParams();
  const loginLight = lighten(theme.palette.button.login, 0.1);
  const loginDark = darken(theme.palette.button.login, 0.05);

  const [open, setOpen] = useState(true);
  const handleClose = () => {
    setOpen(false);
    navigate({
      pathname: location.pathname,
      search: "", // remove query string
    }, { replace: true }); // replace so it doesn’t add to browser history
  }

  // const bonusSliderImage_en = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  // const bonusSliderImage_bd = [
  //   process.env.REACT_APP_BONUS_SLIDER + "welcome.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "unlimited_deposit.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "slot_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "live_and_sport_weekly.jpg",
  //   process.env.REACT_APP_BONUS_SLIDER + "referral.jpg",
  // ];

  const BONUS_SLIDER_BASE = process.env.REACT_APP_BONUS_SLIDER || "";

  const isOtpInDetail = process.env.REACT_APP_REGISTER_OTP_IN_DETAIL === "true";

  function getBonusSliderImages(envVar) {
    if (!envVar) return [];
    return envVar.split(",").map(function (fileName) {
      return BONUS_SLIDER_BASE + fileName.trim();
    });
  }

  const bonusSliderImage_en = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_EN);
  const bonusSliderImage_bd = getBonusSliderImages(process.env.REACT_APP_BONUS_SLIDER_IMAGES_BD);

  const isEmailEnabled = process.env.REACT_APP_EMAIL_REGISTER !== "false";
  const isDobEnabled = process.env.REACT_APP_DOB_REGISTER !== "false";

  const {
    data: commonBackground,
    isLoading: isCommonBackgroundLoading,
    isSuccess: isCommonBackgroundSuccess,
  } = useGetCommonBackgroundQuery({ prefetch: true });

  const {
    data: bankDetails,
    isLoading: isBankDetailsLoading,
    isError: isBankDetailsError,
    isSuccess: isBankDetailsSuccess,
    error: bankDetailsError,
  } = useGetPublicBankAccountsQuery();

  const registerDetailSchema = yup.object().shape({
    username: yup
      .string()
      .required(t("validation.username"))
      .test(
        "len",
        "Username must be between 8 to 20 characters.",
        (val) => val?.length >= 8 && val?.length <= 20
      ),
    password: yup
      .string()
      .required(t("validation.password"))
      .test(
        "len",
        t("validation.passwordlength"),
        (val) => val?.length >= 8 && val?.length <= 20
      ),
    confirmPassword: yup
      .string()
      .required(t("validation.confirmpassword"))
      .oneOf([yup.ref("password"), null], t("validation.passwordnotmatch")),

    // Include mobile + otp when enabled
    ...(isOtpInDetail && {
      mobileNumber: yup
        .string()
        .required(t("validation.mobilenum"))
        .test(
          "validTel",
          t("validation.mobilenumlength"),
          (val) => (val ? matchIsValidTel(val) : false)
        ),
      otp: yup.string().when("showOtp", {
        is: () => commonBackground?.result?.otpFlag === "Y",
        then: yup.string().required(t("validation.otp")),
        otherwise: yup.string(),
      }),
    }),
  });

  const registerProfileSchema = yup.object().shape({
    fullname: yup.string().required(t("validation.fullname")),

    ...(isEmailEnabled && {
      email: yup
        .string()
        .email(t("validation.invalidemail"))
        .required(t("validation.emailisrequired")),
    }),

    ...(!isOtpInDetail && {
      mobileNumber: yup
        .string()
        .required(t("validation.mobilenum"))
        .test(
          "validTel",
          t("validation.mobilenumlength"),
          (val) => (val ? matchIsValidTel(val) : false)
        ),

      otp: yup.string().when("showOtp", {
        is: () => commonBackground?.result?.otpFlag === "Y",
        then: yup.string().required(t("validation.otp")),
        otherwise: yup.string(),
      }),
    }),

    // ...(process.env.REACT_APP_BANK_REGISTER === "true" && {
    //   bankcode: yup.string(),
    //   bankaccountnumber: yup.string().when("bankcode", {
    //     is: (bankcode) => !!bankcode,
    //     then: yup
    //       .string()
    //       .required(t("validation.bankaccno"))
    //       .matches(/^[0-9]+$/, t("validation.bankaccnoonlynumbers")),
    //     otherwise: yup.string(),
    //   }),
    // }),
  });

  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();

    return result;
  };

  const [register, { data, isLoading, isError, isSuccess, error, reset }] =
    useRegisterAccountMutation();

  const [addFingerprint, { fingerprintData }] =
    useAddFingerprintMutation();

  const [registerBank, {
    data: dataBank,
    isLoading: isLoadingBank,
    isError: isErrorBank,
    isSuccess: isSuccessBank,
    error: errorBank,
    reset: resetBank
  }] = useRegisterAccountWithBankMutation();

  const [
    getOtp,
    {
      data: otp,
      isFetching: isOtpFetching,
      isLoading: isOtpLoading,
      isError: isOtpError,
      isSuccess: isOtpSuccess,
      error: otpError,
    },
  ] = useLazyGetOtpQuery();

  const {
    control: detailControl,
    handleSubmit: handleSubmitDetail,
    getValues: getDetailValues,
    trigger: triggerDetail,
  } = useForm({
    resolver: yupResolver(registerDetailSchema),
  });

  const {
    control: profileControl,
    handleSubmit: handleSubmitProfile,
    getValues,
    trigger: triggerProfile,
    watch: watchProfile,
    setValue: setProfileFieldValue,
  } = useForm({
    resolver: yupResolver(registerProfileSchema),
  });

  // const selectedBankCode = watchProfile("bankcode");

  // useEffect(() => {
  //   if (!selectedBankCode) {
  //     setProfileFieldValue("bankaccountnumber", "");
  //   }
  // }, [selectedBankCode, setProfileFieldValue]);

  const submitDetail = async (data) => {
    setDetailValue({ onThisPage: false, ...data });
  };

  useEffect(() => {
    const clickid = searchParams.get("clickid");
    if (clickid) {
      Cookies.set("clickid", clickid, { expires: 7, path: "/" });
    }
  }, []);

  const submitProfile = async (data) => {
    const fbc = Cookies.get("_fbc");
    const fbp = Cookies.get("_fbp");
    const clickId = Cookies.get("clickid") || Cookies.get("rtkclickid-store");

    setProfileValue({ ...data });

    // Prefer profile form data, fallback to detail form values
    const finalMobileNumber =
      data?.mobileNumber?.replace(/[^A-Z0-9]+/gi, "") ||
      detailValue?.mobileNumber?.replace(/[^A-Z0-9]+/gi, "");
    const finalOtp = data?.otp || detailValue?.otp;

    // Prepare shared fields
    const sharedData = {
      username: detailValue?.username,
      password: detailValue?.password,
      // When the DOB field is hidden, still send a default so the backend
      // (which requires dateOfBirth) doesn't reject the registration.
      dateOfBirth: isDobEnabled ? data?.date : "1990-01-01",
      mobileNumber: finalMobileNumber,
      referralCode: data?.referralCode,
      otp: finalOtp,
      fbc,
      fbp,
      clickId,
    };

    if (process.env.REACT_APP_BANK_REGISTER) {
      // const { bankcode, bankaccountnumber, ...restData } = data;
      const formData = {
        // ...restData,
        ...data,
        ...sharedData,
        // ...(bankcode && {
        //   bankName: bankcode,
        //   bankAccountNumber: bankaccountnumber,
        // }),
        // ...(process.env.REACT_APP_COUNTRY === "KR" && {
        //   idNo: data?.idNo,
        //   telCo: data?.telCo,
        // }),
      };
      await register(formData).unwrap();

      // await registerBank(formData).unwrap();
    } else {
      const formData = {
        ...data,
        ...sharedData,
      };

      await register(formData).unwrap();
    }

    localStorage.clear();
  };

  useEffect(() => {
    if (!isLoading && isSuccess) {
      (async () => {
        window.registerListener = window.registerListener || window;
        dispatch(
          setAlert({
            isOpen: true,
            message: t("header.registersuccessmessage"),
            variant: "success",
          })
        );

        dispatch(setCredentials({ ...data?.result }));

        try {
          const fingerprint = await getFingerprint();

          await addFingerprint({
            fingerprint: fingerprint.visitorId,
            userAgent: navigator.userAgent,
          });
        } catch (fingerprintError) {
          console.error("Fingerprint collection failed:", fingerprintError);
        }

        reset();

        // window.fbq('track', 'CompleteRegistration');
        // window.bge('event', 'ec_register', {configId: '904500226561349376'});

        const registerCompleteMessage = { event: 'af_complete_registration' };
        window.registerListener.postMessage(JSON.stringify(registerCompleteMessage));

        navigate(Constant.getPath("/"), { state: { justRegistered: true } });
      })();
    }
  }, [isLoading, isSuccess]);

  useEffect(() => {
    if (!isLoadingBank && isSuccessBank) {
      window.registerListener = window.registerListener || window;
      dispatch(
        setAlert({
          isOpen: true,
          message: t("header.registersuccessmessage"),
          variant: "success",
        })
      );

      dispatch(setCredentials({ ...dataBank?.result }));
      resetBank();

      // window.fbq('track', 'CompleteRegistration');
      // window.bge('event', 'ec_register', {configId: '904500226561349376'});

      const registerCompleteMessage = { event: 'af_complete_registration' };
      window.registerListener.postMessage(JSON.stringify(registerCompleteMessage));

      navigate(Constant.getPath("/"), { state: { justRegistered: true } });
    }
  }, [isLoadingBank, isSuccessBank])


  useEffect(() => {
    if (!isLoading && isError) {
      dispatch(
        setAlert({
          isOpen: true,
          message: error?.data?.message,
          variant: "error",
        })
      );
      reset();
    }
  }, [isLoading, isError])

  useEffect(() => {
    if (!isLoadingBank && isErrorBank) {
      dispatch(
        setAlert({
          isOpen: true,
          message: errorBank?.data?.message,
          variant: "error",
        })
      );
      resetBank();
    }
  }, [isLoadingBank, isErrorBank])

  useEffect(() => {
    if (!isOtpLoading && isOtpSuccess) {
      dispatch(
        setAlert({
          isOpen: true,
          message: "Otp is sent",
          variant: "success",
        })
      );
    }

    if (!isOtpLoading && isOtpError) {
      dispatch(
        setAlert({
          isOpen: true,
          message: otpError?.data?.message,
          variant: "error",
        })
      );
    }
  }, [isOtpLoading, isOtpSuccess, isOtpError]);

  // not used for now, for one page registration
  const renderOneStepSection = () => (
    <form onSubmit={handleSubmitDetail(submitDetail)}>
      <FormTextField
        label={t("mobileloginregister.username")}
        control={detailControl}
        defaultValue={detailValue?.username}
        controllerName="username"
        size="small"
      />
      <FormTextField
        label={t("mobileloginregister.password")}
        control={detailControl}
        defaultValue={detailValue?.password}
        controllerName="password"
        type="password"
        showPasswordToggle
        size="small"
      />
      <FormTextField
        label={t("mobileloginregister.reenterpassword")}
        control={detailControl}
        value={detailValue?.confirmPassword}
        controllerName="confirmPassword"
        type="password"
        showPasswordToggle
        size="small"
      />
      {isOtpInDetail && (
        <>
          <Box sx={{ position: "relative" }}>
            <FormPhoneNumber
              label={t("mobileloginregister.mobilenumber")}
              defaultValue={profileValue?.phoneNumber}
              control={detailControl}
              controllerName="mobileNumber"
              size="small"
            />

            {commonBackground?.result?.otpFlag === "Y" && (
              <>
                <Box alignSelf="center">
                  <Button
                    sx={{
                      position: "absolute",
                      right: 0,
                      marginTop: "17px",
                      marginRight: "1px",
                      zIndex: 10,
                      paddingLeft: "10px",
                      paddingRight: "10px",
                      background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
                    }}
                    variant="contained"
                    onClick={() => {
                      const mobileNumber = getDetailValues("mobileNumber")?.replace(/[^A-Z0-9]+/gi, "");
                      if (mobileNumber) {
                        getOtp(mobileNumber);
                      }
                    }}
                  >
                    {t("mobileloginregister.getotp")}
                  </Button>
                </Box>

                <FormTextField
                  label={t("mobileloginregister.otp")}
                  defaultValue={profileValue?.otp || ""}
                  control={detailControl}
                  controllerName="otp"
                  type="text"
                  size="small"
                />
              </>
            )}
          </Box>
        </>
      )}
      <Box display="flex" flexDirection="column">
        <FormControl>
          <FormTextField
            label={t("mobileloginregister.fullname")}
            defaultValue={profileValue?.fullname || ""}
            control={profileControl}
            controllerName="fullname"
            size="small"
            note={t("mobileloginregister.fullnamenote")}
          />
          {/* {process.env.REACT_APP_BANK_REGISTER && (
            <>
              <FormSelect
                label={t("walletcard.bankaccountpage.bankname")}
                control={profileControl}
                controllerName="bankcode"
                items={bankDetails?.result?.bankConfigs
                  .map((item) => item.bankCode)}
                size="small"
                allowEmpty
                emptyLabel={t("walletcard.withdrawalpage.selectbank")}
              />
              {selectedBankCode && (
                <FormTextField
                  label={t("walletcard.withdrawalpage.bankaccountnumber")}
                  defaultValue={profileValue?.bankaccountnumber || ""}
                  control={profileControl}
                  controllerName="bankaccountnumber"
                  type="text"
                  size="small"
                />
              )}
            </>
          )} */}
          {process.env.REACT_APP_COUNTRY === "KR" && (
            <>
              <FormTextField
                label={t("mobileloginregister.idno")}
                control={profileControl}
                controllerName="idNo"
                type="text"
                size="small"
                note={t("mobileloginregister.idnote")}
              />
            </>
          )}
          {isEmailEnabled && (
            <FormTextField
              label={t("mobileloginregister.email")}
              defaultValue={profileValue?.email || ""}
              control={profileControl}
              controllerName="email"
              type="email"
              size="small"
            />
          )}
          {isDobEnabled && (
            <FormDatePicker
              control={profileControl}
              label={t("mobileloginregister.dob")}
              value="1990-01-01"
              time={false}
              size="small"
            />
          )}
          {process.env.REACT_APP_COUNTRY === "KR" && (
            <>
              <FormSelect
                label={t("mobileloginregister.telco")}
                control={profileControl}
                controllerName="telCo"
                items={Object.entries(
                  t("mobileloginregister.telcoOptions", { returnObjects: true }) ?? {}
                ).map(([value, label]) => ({
                  value,
                  label,
                }))}
                size="small"
              />
            </>
          )}
          {!isOtpInDetail && (
            <>
              <FormPhoneNumber
                label={t("mobileloginregister.mobilenumber")}
                defaultValue={profileValue?.phoneNumber}
                control={profileControl}
                controllerName="mobileNumber"
                size="small"
              />

              {commonBackground?.result?.otpFlag === "Y" && (
                <>
                  <Box alignSelf="center">
                    <Button
                      sx={{
                        position: "absolute",
                        right: 0,
                        marginTop: "17px",
                        marginRight: "1px",
                        zIndex: 10,
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
                      }}
                      variant="contained"
                      onClick={async () => {
                        const valid = await triggerProfile("mobileNumber");
                        if (valid) {
                          const mobileNumber = getValues().mobileNumber?.replace(/[^A-Z0-9]+/gi, "");
                          getOtp(mobileNumber);
                        }
                      }}
                    >
                      {t("mobileloginregister.getotp")}
                    </Button>
                  </Box>

                  <FormTextField
                    label={t("mobileloginregister.otp")}
                    defaultValue={profileValue?.otp || ""}
                    control={profileControl}
                    controllerName="otp"
                    type="text"
                    size="small"
                  />
                </>
              )}
            </>
          )}
          {localStorage.getItem("refCode") ? (
            <FormTextField
              label={t("mobileloginregister.refcode")}
              defaultValue={localStorage.getItem("refCode")}
              control={profileControl}
              controllerName="referralCode"
              type="text"
              size="small"
              disabled={true}
            />
          ) : null}

        </FormControl>
      </Box>
      <Box display="flex" justifyContent="center" mt="30px" paddingBottom="40px">
        <Button variant="contained" type="submit"
          sx={{
            // color: process.env.REACT_APP_COLOR_TEXT,
            background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
            // fontWeight: 'bold',
            px: 4,
            py: 1,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          }}
        >
          {t("mobileloginregister.register")}
        </Button>
      </Box>
    </form>
  );

  const renderDetailSection = () => (
    <form onSubmit={handleSubmitDetail(submitDetail)}>
      <Box display="flex" alignItems="center">
        <PersonIcon
          sx={{
            color: process.env.REACT_APP_COLOR_TEXT || undefined,
            mt: 2,
            mr: 1,
          }}
        />
        <Box flex={1}>
          <FormTextField
            label={t("mobileloginregister.username")}
            control={detailControl}
            defaultValue={detailValue?.username}
            controllerName="username"
            size="small"
          />
        </Box>
      </Box>

      <Box display="flex" alignItems="center">
        <KeyIcon
          sx={{
            color: process.env.REACT_APP_COLOR_TEXT || undefined,
            mt: 2,
            mr: 1,
          }}
        />
        <Box flex={1}>
          <FormTextField
            label={t("mobileloginregister.password")}
            control={detailControl}
            defaultValue={detailValue?.password}
            controllerName="password"
            type="password"
            showPasswordToggle
            size="small"
          />
        </Box>
      </Box>

      <Box sx={{ paddingLeft: 4 }}>
        <FormTextField
          label={t("mobileloginregister.reenterpassword")}
          control={detailControl}
          value={detailValue?.confirmPassword}
          controllerName="confirmPassword"
          type="password"
          showPasswordToggle
          size="small"
        />
        {isOtpInDetail && (
          <>
            <Box sx={{ position: "relative" }}>
              <FormPhoneNumber
                label={t("mobileloginregister.mobilenumber")}
                defaultValue={profileValue?.phoneNumber}
                control={detailControl}
                controllerName="mobileNumber"
                size="small"
              />

              {commonBackground?.result?.otpFlag === "Y" && (
                <>
                  <Box alignSelf="center">
                    <Button
                      sx={{
                        position: "absolute",
                        right: 0,
                        marginTop: "17px",
                        marginRight: "1px",
                        zIndex: 10,
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
                      }}
                      variant="contained"
                      onClick={async () => {
                        const valid = await triggerDetail(["username", "password", "confirmPassword", "mobileNumber"]);
                        if (valid) {
                          const mobileNumber = getDetailValues("mobileNumber")?.replace(/[^A-Z0-9]+/gi, "");
                          getOtp(mobileNumber);
                        }
                      }}
                    >
                      {t("mobileloginregister.getotp")}
                    </Button>
                  </Box>

                  <FormTextField
                    label={t("mobileloginregister.otp")}
                    defaultValue={profileValue?.otp || ""}
                    control={detailControl}
                    controllerName="otp"
                    type="text"
                    size="small"
                  />
                </>
              )}
            </Box>
          </>
        )}
      </Box>
      <Box display="flex" justifyContent="center" mt="30px" paddingBottom="30px">
        <Button variant="contained" type="submit"
          sx={{
            // color: process.env.REACT_APP_COLOR_TEXT,
            background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
            // fontWeight: 'bold',
            px: 4,
            py: 1,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          }}
        >
          {t("mobileloginregister.next")}
        </Button>
      </Box>
    </form>
  );

  const renderProfileSection = () => (
    <form onSubmit={handleSubmitProfile(submitProfile)}>
      <Box display="flex" flexDirection="row" alignItems="center" mt="10px">
        <IconButton
          edge="start"
          onClick={() => setDetailValue({ onThisPage: true })}
        >
          <ArrowBackIcon sx={{ color: process.env.REACT_APP_COLOR_TEXT || undefined }} />
        </IconButton>
        <Typography py="5px" color={process.env.REACT_APP_COLOR_TEXT || "white"}>
          {" "}
          {t("mobileloginregister.back")}
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column">
        <FormControl>
          <FormTextField
            label={t("mobileloginregister.fullname")}
            defaultValue={profileValue?.fullname || ""}
            control={profileControl}
            controllerName="fullname"
            size="small"
            note={t("mobileloginregister.fullnamenote")}
          />
          {/* {process.env.REACT_APP_BANK_REGISTER && (
            <>
              <FormSelect
                label={t("walletcard.bankaccountpage.bankname")}
                control={profileControl}
                controllerName="bankcode"
                items={bankDetails?.result?.bankConfigs
                  .map((item) => item.bankCode)}
                size="small"
                allowEmpty
                emptyLabel={t("walletcard.withdrawalpage.selectbank")}
              />
              {selectedBankCode && (
                <FormTextField
                  label={t("walletcard.withdrawalpage.bankaccountnumber")}
                  defaultValue={profileValue?.bankaccountnumber || ""}
                  control={profileControl}
                  controllerName="bankaccountnumber"
                  type="text"
                  size="small"
                />
              )}
            </>
          )} */}
          {process.env.REACT_APP_COUNTRY === "KR" && (
            <>
              <FormTextField
                label={t("mobileloginregister.idno")}
                control={profileControl}
                controllerName="idNo"
                type="text"
                size="small"
                note={t("mobileloginregister.idnote")}
              />
            </>
          )}
          {isEmailEnabled && (
            <FormTextField
              label={t("mobileloginregister.email")}
              defaultValue={profileValue?.email || ""}
              control={profileControl}
              controllerName="email"
              type="email"
              size="small"
            />
          )}
          {isDobEnabled && (
            <FormDatePicker
              control={profileControl}
              label={t("mobileloginregister.dob")}
              value="1990-01-01"
              time={false}
              size="small"
            />
          )}
          {process.env.REACT_APP_COUNTRY === "KR" && (
            <>
              <FormSelect
                label={t("mobileloginregister.telco")}
                control={profileControl}
                controllerName="telCo"
                items={Object.entries(
                  t("mobileloginregister.telcoOptions", { returnObjects: true }) ?? {}
                ).map(([value, label]) => ({
                  value,
                  label,
                }))}
                size="small"
              />
            </>
          )}
          {!isOtpInDetail && (
            <>
              <FormPhoneNumber
                label={t("mobileloginregister.mobilenumber")}
                defaultValue={profileValue?.phoneNumber}
                control={profileControl}
                controllerName="mobileNumber"
                size="small"
              />

              {commonBackground?.result?.otpFlag === "Y" && (
                <>
                  <Box alignSelf="center">
                    <Button
                      sx={{
                        position: "absolute",
                        right: 0,
                        marginTop: "17px",
                        marginRight: "1px",
                        zIndex: 10,
                        paddingLeft: "10px",
                        paddingRight: "10px",
                        background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
                      }}
                      variant="contained"
                      onClick={async () => {
                        const valid = await triggerProfile("mobileNumber");
                        if (valid) {
                          const mobileNumber = getValues().mobileNumber?.replace(/[^A-Z0-9]+/gi, "");
                          getOtp(mobileNumber);
                        }
                      }}
                    >
                      {t("mobileloginregister.getotp")}
                    </Button>
                  </Box>

                  <FormTextField
                    label={t("mobileloginregister.otp")}
                    defaultValue={profileValue?.otp || ""}
                    control={profileControl}
                    controllerName="otp"
                    type="text"
                    size="small"
                  />
                </>
              )}
            </>
          )}
          {localStorage.getItem("refCode") ? (
            <FormTextField
              label={t("mobileloginregister.refcode")}
              defaultValue={localStorage.getItem("refCode")}
              control={profileControl}
              controllerName="referralCode"
              type="text"
              size="small"
              disabled={true}
            />
          ) : null}

        </FormControl>
      </Box>
      <Box display="flex" justifyContent="center" mt="30px" paddingBottom="40px">
        <Button variant="contained" type="submit"
          sx={{
            // color: process.env.REACT_APP_COLOR_TEXT,
            background: `linear-gradient(to bottom, ${loginLight} 50%, ${loginDark} 50%)`,
            // fontWeight: 'bold',
            px: 4,
            py: 1,
            borderRadius: '8px',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
            },
          }}
        >
          {t("mobileloginregister.register")}
        </Button>
      </Box>
    </form>
  );

  const gradient1 = process.env.REACT_APP_COLOR_HEADER_GRADIENT1?.trim();
  const gradient2 = process.env.REACT_APP_COLOR_HEADER_GRADIENT2?.trim();
  const headerImage = process.env.REACT_APP_MOBILE_HEADER_IMAGE?.trim();
  let headerColor;
  if (gradient1 && gradient2) {
    headerColor = `linear-gradient(to right, ${gradient1}, ${gradient2})`;
  } else if (headerImage) {
    headerColor = `url(${headerImage})`;
  } else {
    headerColor = theme.palette.header;
  }

  return (
    <Modal open={open} onClose={handleClose}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <Box
        sx={{
          backgroundColor:
            process.env.REACT_APP_COLOR_BACKGROUND_TERTIARY ||
            theme.palette.background.paper,
          minHeight: mobileView ? "100vh" : "auto",
          width: mobileView ? "100%" : 500, // ✅ fixed width for modal
          borderRadius: mobileView ? 0 : '10px',
          p: mobileView ? 0 : 0,
          // display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          mx: "auto",
          boxShadow: mobileView ? "none" : theme.shadows[5],
          position: "relative",
          maxHeight: "90vh",
          overflowY: 'auto',
        }}
      >
        <Toolbar
          sx={{
            background: headerColor,
            borderRadius: mobileView ? 0 : "10px 10px 0 0",
            px: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              flexWrap: "nowrap",          // ✅ prevent wrapping
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,              // ✅ prevent logo shrinking weirdly
              }}
            >
              <img
                src={process.env.REACT_APP_MOBILE_HEADER_LOGO}
                onClick={() => navigate(Constant.getPath("/"))}
                alt={Constant.ALT_TAG_LOGO}
                style={{
                  cursor: "pointer",
                  maxWidth: "120px",
                  height: "auto",
                }}
              />
            </Box>

            {/* Spacer */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Login button */}
            <Button
              variant="outlined"
              disableRipple
              sx={{
                mr: 1,
                whiteSpace: "nowrap",       // ✅ keep text in one line
                borderRadius: "3px",
                color: process.env.REACT_APP_COLOR_PRIMARY,
              }}
              onClick={() =>
                navigate({
                  pathname: location.pathname,
                  search: "?login=1",
                })
              }
            >
              {t("home.login")}
            </Button>

            {/* Close button */}
            <IconButton
              onClick={handleClose}
              sx={{
                flexShrink: 0,              // ✅ never wrap
              }}
            >
              <Close sx={{ color: process.env.REACT_APP_COLOR_MOBILELAYOUT_TEXT || process.env.REACT_APP_COLOR_TEXT }} />
            </IconButton>
          </Box>
        </Toolbar>
        <Divider></Divider>
        <Box sx={{ p: !mobileView ? 3 : 1 }}>
          {mobileView &&
            <ImageCarousel
              banners={lang === "en" ? bonusSliderImage_en : bonusSliderImage_bd}
              navigation={false}
              spaceBetween={0}
            />
          }

          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              marginTop: !mobileView ? 0 : 2,
              marginBottom: 2,
              fontWeight: 'bold',
              color: process.env.REACT_APP_COLOR_PRIMARY,
              textTransform: 'uppercase',
            }}>
            {t("mobileloginregister.register")}
          </Typography>
          <Box
            sx={{
              backgroundColor: process.env.REACT_APP_IS_LIGHT_THEME === 'true'
                ? 'rgba(0, 0, 0, 0.05)'  // darken for light theme
                : 'rgba(255, 255, 255, 0.05)', // lighten for dark theme
              borderRadius: 2, // rounded corners
              px: 2,
              marginX: 2,
              marginTop: 2,
              paddingTop: 1,
              marginBottom: 3,
              backdropFilter: 'blur(6px)',
              border: process.env.REACT_APP_IS_LIGHT_THEME === 'true' ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid rgba(255, 255, 255, 0.15)',
              // boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
              borderRadius: '10px',

            }}
          >
            {detailValue.onThisPage
              ? renderDetailSection()
              : renderProfileSection()}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
};

export default Register;
